<template>
  <Transition name="slide">
    <div
      v-if="open"
      ref="panelRef"
      :class="[
        'side-panel fixed z-40 flex flex-col border-l bg-background shadow-lg',
        'sm:max-w-none',
        !isMobile && !resizable && sizeClass,
        side === 'left' ? 'left-0 border-l-0 border-r' : 'right-0',
      ]"
      :style="panelStyle"
    >
      <!-- Resize handle (hidden on mobile) -->
      <div
        v-if="isResizableActive"
        :class="[
          'resize-handle absolute bottom-0 top-0 z-50 w-1 cursor-col-resize transition-colors',
          'hover:bg-primary/50',
          isResizing && 'bg-primary/50',
          side === 'left' ? 'right-0' : 'left-0',
        ]"
        @pointerdown="startResize"
      >
        <div
          :class="[
            'absolute top-1/2 h-8 w-1 -translate-y-1/2 rounded-full bg-muted-foreground/30',
            side === 'left' ? 'right-0' : 'left-0',
          ]"
        ></div>
      </div>

      <PanelHeader
        :icon="icon"
        :title="title"
        :subtitle="subtitle"
        :show-close="showClose"
        :fixed="fixedHeaderComputed"
        @close="closePanel"
      >
        <template
          v-if="$slots.leftAction"
          #leftAction
        >
          <slot name="leftAction"></slot>
        </template>
        <template
          v-if="$slots.topRight"
          #topRight
        >
          <slot name="topRight"></slot>
        </template>
        <template
          v-if="$slots.headerContent"
          #headerContent
        >
          <slot name="headerContent"></slot>
        </template>
        <template
          v-if="$slots.icon"
          #icon
        >
          <slot name="icon"></slot>
        </template>
        <template
          v-if="$slots.title"
          #title
        >
          <slot name="title"></slot>
        </template>
        <template
          v-if="$slots.subtitle"
          #subtitle
        >
          <slot name="subtitle"></slot>
        </template>
        <template
          v-if="$slots.rightAction"
          #rightAction
        >
          <slot name="rightAction"></slot>
        </template>
      </PanelHeader>

      <div :class="['flex-1', useBodyScroll ? 'overflow-y-auto' : '']">
        <slot></slot>
      </div>

      <div
        v-if="$slots.footerLeft || $slots.footerRight"
        :class="[
          fixedFooterComputed
            ? `
              sticky bottom-0 z-10 border-t bg-secondary backdrop-blur
              supports-[backdrop-filter]:bg-secondary
            `
            : 'border-t bg-muted/30',
        ]"
      >
        <div class="flex items-center justify-between gap-3 px-4 py-2">
          <div class="text-sm">
            <slot name="footerLeft"></slot>
          </div>
          <div class="flex items-center gap-2">
            <slot name="footerRight"></slot>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { type Component, computed, ref } from 'vue'
import { useMediaQuery, useStorage } from '@vueuse/core'

import PanelHeader from '@/components/PanelHeader/index.vue'
import { NAVBAR_HEIGHT } from '@/constants'

const props = withDefaults(
  defineProps<{
    open: boolean
    icon?: Component
    title?: string
    subtitle?: string
    size?: string
    side?: 'left' | 'right'
    showClose?: boolean
    fixedHeader?: boolean
    fixedFooter?: boolean
    resizable?: boolean
    storageKey?: string
    defaultWidth?: number
    minWidth?: number
    maxWidth?: number
  }>(),
  {
    side: 'right',
    resizable: false,
    defaultWidth: 400,
    minWidth: 280,
    maxWidth: 800,
  },
)

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'close'): void
  (e: 'resize', width: number): void
}>()

const panelRef = ref<HTMLElement | null>(null)
const isResizing = ref(false)

// Mobile detection (sm breakpoint = 640px)
const isMobile = useMediaQuery('(max-width: 639px)')

// Resizable only on desktop
const isResizableActive = computed(() => props.resizable && !isMobile.value)

// Persisted width in localStorage
const storageKeyComputed = computed(() => props.storageKey ?? `side-panel-width-${props.side}`)
const storedWidth = useStorage(storageKeyComputed, () => props.defaultWidth)

const fixedHeaderComputed = computed(() => props.fixedHeader !== false)
const fixedFooterComputed = computed(() => props.fixedFooter !== false)

// Always allow scrolling when header is fixed
const useBodyScroll = computed(() => fixedHeaderComputed.value)

const sizeClass = computed(() => props.size ?? 'w-full md:w-[400px]')

const NAV_H = NAVBAR_HEIGHT
const panelStyle = computed(() => {
  const base: Record<string, string> = {
    top: `${NAV_H}px`,
    height: `calc(100vh - ${NAV_H}px)`,
  }

  // On mobile: 100% width, on desktop: stored width if resizable
  if (isMobile.value) {
    base.width = '100%'
  } else if (props.resizable) {
    base.width = `${storedWidth.value}px`
  }

  return base
})

function startResize(event: PointerEvent) {
  if (!isResizableActive.value) return

  event.preventDefault()
  isResizing.value = true

  const startX = event.clientX
  const startWidth = storedWidth.value

  const onPointerMove = (e: PointerEvent) => {
    const delta = props.side === 'right' ? startX - e.clientX : e.clientX - startX
    const newWidth = Math.min(props.maxWidth, Math.max(props.minWidth, startWidth + delta))
    storedWidth.value = newWidth
    emit('resize', newWidth)
  }

  const onPointerUp = () => {
    isResizing.value = false
    document.removeEventListener('pointermove', onPointerMove)
    document.removeEventListener('pointerup', onPointerUp)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  document.addEventListener('pointermove', onPointerMove)
  document.addEventListener('pointerup', onPointerUp)
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}

function closePanel() {
  emit('update:open', false)
  emit('close')
}
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.side-panel[class*="right-0"] {
  &.slide-enter-from,
  &.slide-leave-to {
    transform: translateX(100%);
  }
}

.side-panel[class*="left-0"] {
  &.slide-enter-from,
  &.slide-leave-to {
    transform: translateX(-100%);
  }
}

.resize-handle {
  touch-action: none;
}
</style>
